import {defineStore, storeToRefs} from 'pinia';
import {computed, h, onScopeDispose, ref} from 'vue';
import type {TreeOption} from 'naive-ui';
import {NInput} from 'naive-ui';
import {
    SketchDisplayState,
    ReferenceCreationContext,
    filterModelBodyTreeProjection,
    resolveModelBodyProjectionState,
} from '@puppycad/cad-core';
import type {
    CadCommand,
    FeatureTreeProjectionSnapshot,
    WorkerGeometryStateSummary,
    GeometryObject,
    AssemblyMateDocumentModel,
} from '@puppycad/cad-core';
import {useDrawingStore} from './useDrawing';
import {usePlaneStore} from './usePlane';
import {useSketchStateStore} from './useSketchState';
import {useViewer} from './useViewer';
import {usePartTabsStore} from '@/stores';
import {createFeatureTreeActions} from './feature-tree/create-feature-tree-actions';
import {
    buildFeatureTreeProjection,
} from './feature-tree/build-feature-tree-projection';
import {
    buildAssemblyMateTreeProjection,
    mergeAssemblyMateFoldersIntoTree,
} from './feature-tree/build-assembly-mate-tree-projection';
import {
    buildAssemblyDatumReferenceTreeProjection,
} from './feature-tree/build-assembly-datum-reference-tree-projection';
import {
    mergeAssemblyDatumReferenceTree,
} from './feature-tree/merge-assembly-datum-reference-tree';
import {
    collectManagedSketches,
    getObjectDisplayName,
    resolveFeatureTreeObjectSummary,
    resolveSketchEntityTypeByMeshClass,
    syncSketchSerialByManagedSketches,
} from './feature-tree/feature-tree-object-helpers';
import {resolveFeatureRollbackBarDisabled} from './feature-tree/resolve-feature-rollback-bar-disabled';
import {
    FeatureTreeDataSource,
} from './feature-tree/feature-tree-types';
import type {
    FeatureTreeNodeMeta,
    FeatureTreeObjectSummary,
    FeatureTreeDatumAxisSummary,
    FeatureTreeDatumPlaneSummary,
    FeatureTreeDatumPointSummary,
    FeatureTreeProviders,
    ManagedSketchSummary,
    SketchLifecycleEntry,
} from './feature-tree/feature-tree-types';
import {logger} from '@/utils';
import {resolveUniqueBodyTreeNodeLabel} from '@/utils/feature-tree';
import {AssemblyMateDiagnosticPresentationIndex, AssemblyMateTreeProjectionIndex, FeatureTreePresentationIndex, FeatureTreeNameProjectionIndex, FeatureTreeRuntimeSubtreeProjector, FeatureTreeRuntimeSubtreeReplay} from '@/core/feature-tree/presentation';

export type {
    FeatureTreeFeatureDeleteActionInput,
    FeatureTreeFeatureVisibilityActionInput,
    FeatureTreeNodeMeta,
    FeatureTreeProviders,
    SketchLifecycleEntry,
} from './feature-tree/feature-tree-types';
export {FeatureTreeDataSource, FeatureTreeNodeKind} from './feature-tree/feature-tree-types';

/**
 * GLB 子树提供者返回结构。
 *
 * 说明：
 * 1. 该类型刻意以结构化类型（ structural typing ）定义在 store 侧，
 *    避免 `useFeatureTree` 反向依赖 `useImportedModelFeatureTree`，造成循环引用；
 * 2. `replaceTree` 用于装配体 / 分组标签：provider 拥有装配层级权威树，
 *    `refreshTree()` 跳过零件原生投影，并只合并装配体来源的正式基准对象与配合派生节点；
 * 3. 零件标签页不设置 `replaceTree`，走合并路径：
 *    - `bodyChildren` 合并到原生 `solid-bodies-folder.children` 末尾，让 GLB 实体在「实体(N)」里参与计数；
 *    - `subtreeChildren` 追加到 part-root.children 的末尾，保持系统节点先于零件/装配结构；
 * 4. `.puppy` 零件导入也必须走同一合并路径，避免“插入到当前场景”时清掉原生实体；
 * 5. `nodeMapEntries` 在 merge 路径下被 merge 进 featureTreeNodeMap，
 *    在 replace 路径下则先 clear 再写入，保证 GLB 绑定与原生绑定各自正确。
 */
export interface GlbMergeSubtreeProviderResult {
    replaceTree?: boolean;
    subtreeChildren: TreeOption[];
    bodyChildren: TreeOption[];
    nodeMapEntries: Map<string, FeatureTreeNodeMeta>;
}

/**
 * GLB 子树提供者上下文。
 *
 * 设计说明：
 * 1. 最小收口目标是让设计树刷新显式绑定到目标 tab，而不是隐式读取“当前 tab”；
 * 2. `partLabel` 也随上下文一并显式传入，避免投影时退回到 `partTabsStore.activeTab`；
 * 3. 后续若升级为 `treeByTabId`，这里可继续作为按 tab 派生的标准输入。
 */
export interface GlbTreeRefreshProviderContext {
    sceneSketches: ManagedSketchSummary[];
    tabId: string | null;
    partLabel?: string;
}

/**
 * 从设计树节点读取可用于实体文件夹去重的文本标签。
 *
 * @param node 设计树节点。
 * @returns 去除首尾空白后的文本标签；非文本标签返回空串。
 */
function resolveBodyTreeNodeTextLabel(node: TreeOption): string {
    const label = node.label;
    return typeof label === 'string' ? label.trim() : '';
}

/**
 * 合并原生实体节点与导入实体节点，并在同一个实体文件夹边界统一处理重名。
 *
 * 设计说明：
 * 1. 原生特征结果体、普通导入零件实体与 `.puppy` 离线包实体最终都显示在同一个
 *    `solid-bodies-folder` 中，名称唯一性必须在这个共同边界收口；
 * 2. 只调整树节点显示名，不改写 cad-core 的特征名、拓扑绑定 ID 或 Three.js 对象名，
 *    避免 UI 去重污染几何和选择链路。
 *
 * @param bodyChildren 待显示的实体节点，顺序即设计树显示顺序。
 * @returns 标签已唯一化的新实体节点列表。
 */
function resolveUniqueBodyTreeChildren(
    bodyChildren: readonly TreeOption[],
): TreeOption[] {
    const usedLabels = new Set<string>();
    return bodyChildren.map((node, index) => {
        const originalLabel = resolveBodyTreeNodeTextLabel(node) || `实体${index + 1}`;
        const uniqueLabel = resolveUniqueBodyTreeNodeLabel(originalLabel, usedLabels);
        if (uniqueLabel === node.label) {
            return node;
        }
        return {
            ...node,
            label: uniqueLabel,
        };
    });
}

/**
 * 读取设计树节点的稳定 key。
 *
 * @param node 设计树节点。
 * @returns 可用于 nodeMap 查询的字符串 key；缺失时返回 `null`。
 */
function resolveTreeOptionKey(node: TreeOption): string | null {
    const key = node.key;
    if (typeof key === 'string') {
        return key;
    }
    return typeof key === 'number' ? String(key) : null;
}

/**
 * 从树节点元数据读取统一模型体修订 ID。
 *
 * @param meta 设计树节点元数据。
 * @returns 归一化后的模型体修订 ID；缺失时返回空字符串。
 */
function resolveFeatureTreeModelBodyRevisionId(meta: FeatureTreeNodeMeta | undefined): string {
    const modelBodyRevisionId = meta?.modelBodyRevisionId;
    return typeof modelBodyRevisionId === 'string' ? modelBodyRevisionId.trim() : '';
}

/**
 * 收集外部模型实体节点声明的统一修订 ID。
 *
 * @param bodyChildren 即将合并进实体文件夹的外部模型实体节点。
 * @param nodeMapEntries 外部模型 provider 提供的节点元数据。
 * @returns 去重后的模型体修订 ID 集合。
 */
function collectExternalModelBodyRevisionIds(
    bodyChildren: readonly TreeOption[],
    nodeMapEntries: ReadonlyMap<string, FeatureTreeNodeMeta>,
): ReadonlySet<string> {
    const modelBodyRevisionIds = new Set<string>();
    bodyChildren.forEach((node) => {
        const key = resolveTreeOptionKey(node);
        if (!key) {
            return;
        }
        const modelBodyRevisionId = resolveFeatureTreeModelBodyRevisionId(nodeMapEntries.get(key));
        if (modelBodyRevisionId) {
            modelBodyRevisionIds.add(modelBodyRevisionId);
        }
    });
    return modelBodyRevisionIds;
}

/**
 * 判断 Worker 实体节点是否已经被外部场景表示节点覆盖。
 *
 * @param node Worker 投影生成的实体节点。
 * @param nodeMap 当前待提交的节点元数据映射。
 * @param externalRevisionIds 外部场景表示声明的统一修订 ID 集合。
 * @returns 若二者指向同一个精确修订，则返回 true。
 */
function isNativeBodyDuplicatedByExternalRepresentation(
    node: TreeOption,
    nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
    externalRevisionIds: ReadonlySet<string>,
): boolean {
    const key = resolveTreeOptionKey(node);
    if (!key) {
        return false;
    }
    const meta = nodeMap.get(key);
    if (meta?.source !== FeatureTreeDataSource.State || meta.objectType !== 'body') {
        return false;
    }
    const modelBodyRevisionId = resolveFeatureTreeModelBodyRevisionId(meta);
    return Boolean(modelBodyRevisionId && externalRevisionIds.has(modelBodyRevisionId));
}

/**
 * 用 Worker 原生实体投影校准外部实体节点的选择身份、显示所有权与特征名称。
 *
 * 设计说明：
 * 1. GLB hierarchy 只描述离线包显示层级，实体选择主键始终是 modelBodyRevisionId；
 * 2. 特征产出体的 GLB 表面会在重放后释放，因此必须移除其 GLB primitive 高亮目标，
 *    让 cad-core 通过权威修订 ID 命中当前 FeatureResultMesh；
 * 3. 纯导入体继续保留 GLB primitive 高亮目标，以支持共享 Mesh 内的精确分区高亮；
 * 4. 特征产出体名称直接采用同一 Worker 投影生成的实体名称，保证与特征节点命名口径一致。
 *
 * @param glbSubtree 外部 GLB/puppy 实体投影。
 * @param nativeBodyChildren Worker 原生实体投影节点。
 * @param nativeNodeMap Worker 原生节点元数据。
 * @returns 已统一权威身份与显示所有权的外部投影。
 */
function reconcileExternalBodyProjectionWithWorkerBodies(
    glbSubtree: GlbMergeSubtreeProviderResult,
    nativeBodyChildren: readonly TreeOption[],
    nativeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
): GlbMergeSubtreeProviderResult {
    const nativeBodyByRevisionId = new Map<string, [TreeOption, FeatureTreeNodeMeta]>();
    nativeBodyChildren.forEach((node) => {
        const key = resolveTreeOptionKey(node);
        const meta = key ? nativeNodeMap.get(key) : undefined;
        if (meta?.source !== FeatureTreeDataSource.State || meta.objectType !== 'body') {
            return;
        }
        const modelBodyRevisionId = resolveFeatureTreeModelBodyRevisionId(meta);
        if (modelBodyRevisionId && !nativeBodyByRevisionId.has(modelBodyRevisionId)) {
            nativeBodyByRevisionId.set(modelBodyRevisionId, [node, meta]);
        }
    });
    if (nativeBodyByRevisionId.size === 0) {
        return glbSubtree;
    }

    let hasProjectionChange = false;
    const nodeMapEntries = new Map(glbSubtree.nodeMapEntries);
    const bodyChildren = glbSubtree.bodyChildren.map((node) => {
        const key = resolveTreeOptionKey(node);
        const externalMeta = key ? nodeMapEntries.get(key) : undefined;
        const modelBodyRevisionId = resolveFeatureTreeModelBodyRevisionId(externalMeta);
        const nativeBody = modelBodyRevisionId
            ? nativeBodyByRevisionId.get(modelBodyRevisionId)
            : undefined;
        if (!key || !externalMeta || !nativeBody) {
            return node;
        }

        const [nativeNode, nativeMeta] = nativeBody;
        const createdByFeatureId = nativeMeta.createdByFeatureId?.trim() || '';
        const isFeatureOwnedBody = createdByFeatureId.length > 0;
        const nativeLabel = resolveBodyTreeNodeTextLabel(nativeNode);
        const displayName = isFeatureOwnedBody
            ? (nativeLabel || externalMeta.displayName || resolveBodyTreeNodeTextLabel(node) || '实体')
            : externalMeta.displayName;

        nodeMapEntries.set(key, {
            ...externalMeta,
            objectId: modelBodyRevisionId,
            modelBodyId: nativeMeta.modelBodyId ?? externalMeta.modelBodyId,
            modelBodyRevisionId,
            ...(isFeatureOwnedBody
                ? {
                    createdByFeatureId,
                    displayName,
                    highlightTargets: undefined,
                }
                : {}),
        });
        hasProjectionChange = true;
        return {
            ...node,
            ...(isFeatureOwnedBody && displayName ? {label: displayName} : {}),
            refObjectId: modelBodyRevisionId,
        } as TreeOption;
    });

    return hasProjectionChange
        ? {
            ...glbSubtree,
            bodyChildren,
            nodeMapEntries,
        }
        : glbSubtree;
}

/**
 * 移除与 GLB/puppy 实体同源的 Worker 导入基线实体节点。
 *
 * 设计说明：
 * 1. `.puppy` 导入后，Worker 里会恢复 BRep 基线实体供导出和拓扑重建使用；
 * 2. 同一个 body 也会通过 GLB/puppy 设计树节点暴露给 UI，用于选择、高亮和拓扑代理回流；
 * 3. 实体文件夹只能展示一个语义实体，因此这里只按统一修订 ID 精确去重，
 *    删除 Worker 基线节点的树投影和 nodeMap 条目，不影响普通新画实体。
 *
 * @param nativeBodyChildren 原生 Worker 投影实体节点。
 * @param glbBodyChildren GLB/puppy provider 提供的实体节点。
 * @param nodeMap 当前待提交的节点元数据映射；重复节点会从这里同步移除。
 * @param glbNodeMapEntries GLB/puppy provider 提供的节点元数据。
 * @returns 已剔除重复导入基线实体的 Worker 实体节点。
 */
function filterNativeBodyChildrenDuplicatedByGlbBodies(
    nativeBodyChildren: readonly TreeOption[],
    glbBodyChildren: readonly TreeOption[],
    nodeMap: Map<string, FeatureTreeNodeMeta>,
    glbNodeMapEntries: ReadonlyMap<string, FeatureTreeNodeMeta>,
): TreeOption[] {
    const externalRevisionIds = collectExternalModelBodyRevisionIds(glbBodyChildren, glbNodeMapEntries);
    if (externalRevisionIds.size === 0) {
        return [...nativeBodyChildren];
    }

    return nativeBodyChildren.filter((node) => {
        if (!isNativeBodyDuplicatedByExternalRepresentation(node, nodeMap, externalRevisionIds)) {
            return true;
        }
        const key = resolveTreeOptionKey(node);
        if (key) {
            nodeMap.delete(key);
        }
        return false;
    });
}

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/hooks` 目录；
 * 2. 负责设计树 store 的状态组织与依赖注入；
 * 3. 具体的节点投影构建与节点动作逻辑已下沉到 `hooks/feature-tree/`。
 *
 * 文件职责：
 * 1. 维护设计树响应式状态与共享非响应式缓存；
 * 2. 串联 Viewer 门面、Pinia store 与特征树辅助模块；
 * 3. 对外暴露与既有调用面兼容的设计树 store API。
 */
export const useFeatureTreeStore = defineStore('featureTree', () => {
    const {getViewer} = useViewer();
    const planeStore = usePlaneStore();
    const sketchStateStore = useSketchStateStore();
    const drawingStore = useDrawingStore();
    const partTabsStore = usePartTabsStore();

    /**
     * 取当前活动 tab 的 label，给投影路径作为 part-root 显示名。
     * 没有活动 tab（例如纯原生 CAD 模式）时返回 undefined，让投影回落到默认占位文案。
     */
    function getPartLabelByTabId(tabId?: string | null): string | undefined {
        if (tabId) {
            return partTabsStore.tabs.find((tab) => tab.id === tabId)?.label ?? undefined;
        }
        return partTabsStore.activeTab?.label ?? undefined;
    }
    const {viewerMode, pendingSketchToolCommand} = storeToRefs(sketchStateStore);
    const {currentDrawingDatumPlaneId, selectedDatumPlaneId, isSketchPlaneSelected} = storeToRefs(planeStore);
    const {isDrawingMode} = storeToRefs(drawingStore);
    const hasPendingSketchCommand = computed(() => Boolean(pendingSketchToolCommand.value));
    /**
     * 退回控制棒是否处于禁用状态。
     *
     * 设计说明：
     * 草图绘制期间不允许改变 FeatureManager 重建序列，避免未闭合草图命令与
     * cad-core 回退重放状态互相覆盖。这里暴露给组件层做视觉与交互禁用。
     */
    const isFeatureRollbackBarDisabled = computed(() => resolveFeatureRollbackBarDisabled({
        isDrawingMode: isDrawingMode.value,
        viewerMode: viewerMode.value,
    }));

    /** NaiveUI message 实例。 */
    let messageProvider: FeatureTreeProviders['message'] | null = null;
    /** NaiveUI dialog 实例。 */
    let dialogProvider: FeatureTreeProviders['dialog'] | null = null;
    /** 进入草图编辑回调。 */
    let enterSketchEditingOnDatumPlaneProvider: FeatureTreeProviders['enterSketchEditingOnDatumPlane'] | null = null;
    /** 执行草图命令回调。 */
    let handleSketchCommandProvider: FeatureTreeProviders['handleSketchCommand'] | null = null;
    /** 编辑特征回调。 */
    let handleFeatureEditProvider: FeatureTreeProviders['handleFeatureEdit'] | null = null;
    //------ 基准面编辑特征 开始------
    /** 编辑基准面特征回调。 */
    let handleReferencePlaneEditProvider: FeatureTreeProviders['handleReferencePlaneEdit'] | null = null;
    //------ 基准面编辑特征 结束------
    //------ 基准轴编辑特征 开始------
    /** 编辑基准轴特征回调。 */
    let handleReferenceAxisEditProvider: FeatureTreeProviders['handleReferenceAxisEdit'] | null = null;
    //------ 基准轴编辑特征 结束------
    //------ 基准点编辑特征 开始------
    /** 编辑基准点特征回调。 */
    let handleReferencePointEditProvider: FeatureTreeProviders['handleReferencePointEdit'] | null = null;
    //------ 基准点编辑特征 结束------
    /** 编辑草图回调。 */
    let handleFeatureEditSketchProvider: FeatureTreeProviders['handleFeatureEditSketch'] | null = null;
    /** 设计树草图生命周期动作回调。 */
    let handleSketchLifecycleActionProvider: FeatureTreeProviders['handleSketchLifecycleAction'] | null = null;
    //------ 基准对象删除历史 开始------
    /** 设计树基准对象删除动作回调。 */
    let handleDatumReferenceDeleteActionProvider: FeatureTreeProviders['handleDatumReferenceDeleteAction'] | null = null;
    //------ 基准对象删除历史 结束------
    /** 设计树特征删除动作回调。 */
    let handleFeatureDeleteActionProvider: FeatureTreeProviders['handleFeatureDeleteAction'] | null = null;
    /** 设计树装配组件删除动作回调。 */
    let handleAssemblyComponentDeleteActionProvider:
        | FeatureTreeProviders['handleAssemblyComponentDeleteAction']
        | null = null;
    /** 设计树特征显隐动作回调。 */
    let handleFeatureVisibilityActionProvider: FeatureTreeProviders['handleFeatureVisibilityAction'] | null = null;
    /** 退回控制棒动作回调。 */
    let handleFeatureRollbackActionProvider: FeatureTreeProviders['handleFeatureRollbackAction'] | null = null;
    /** 装配体零部件固定/浮动切换回调。 */
    let handleAssemblyComponentFixationChangeProvider:
        | FeatureTreeProviders['handleAssemblyComponentFixationChange']
        | null = null;
    /** 装配体 Mate 编辑回调。 */
    let handleAssemblyMateEditProvider:
        | FeatureTreeProviders['handleAssemblyMateEdit']
        | null = null;
    //------ 线性零部件阵列节点删除 开始------
    /** 线性零部件阵列分组删除回调。 */
    let handleLinearComponentPatternDeleteProvider:
        | FeatureTreeProviders['handleLinearComponentPatternDelete']
        | null = null;
    /** 线性零部件阵列分组编辑回调。 */
    let handleLinearComponentPatternEditProvider:
        | FeatureTreeProviders['handleLinearComponentPatternEdit']
        | null = null;
    //------ 线性零部件阵列节点删除 结束------

    /** 特征树节点数据。 */
    const featureTreeData = ref<TreeOption[]>([]);
    /**
     * 特征树权威投影提交版本。
     *
     * 设计说明：
     * 该版本只表达“投影已提交”，不承载 CAD 主状态，也不作为 Tree 组件的 key。
     * 结构变化通过新的数据数组发布，由 Tree 按节点 key 更新，避免整树强制卸载重建。
     */
    const featureTreeRevision = ref(0);
    /** 当前选中的节点键。 */
    const selectedKeys = ref<string[]>([]);
    /**
     * 当前选中的对象摘要。
     *
     * 说明：这里禁止保存 THREE.Object3D / SketchGroup 等运行时对象，避免 Pinia/Vue
     * 对三维对象做深度响应式代理，引发草图绘制、选择和 undo/redo 后的交互卡顿。
     */
    const selectedObject = ref<FeatureTreeObjectSummary | null>(null);
    /** 编辑零部件模式中需要以蓝色名称显示的设计树节点。 */
    const assemblyEditHighlightedTreeKeys = ref<Set<string>>(new Set());
    /** 是否显示平铺树。 */
    const showFlatTreeView = ref(false);
    /** 设计树是否处于加载中（从进入项目到模型挂载完成）。 */
    const isTreeLoading = ref(false);
    /** 当前展示在右侧面板中的设计树归属 tab。 */
    const activeTreeTabId = ref<string | null>(partTabsStore.activeTabId ?? null);

    /** 节点元数据映射。 */
    const featureTreeNodeMap = new Map<string, FeatureTreeNodeMeta>();
    /** 激活树的节点索引；字段更新不替换结构，也不使 Tree 组件重新挂载。 */
    const presentationIndex = new FeatureTreePresentationIndex();
    /** 仅在结构刷新提交期间使用，提交后立即释放对候选树的引用。 */
    const incomingPresentationIndex = new FeatureTreePresentationIndex();
    /** 原生命名索引与晚到投影分别持有，禁止共享 Vue 代理引用。 */
    const nameProjectionIndex = new FeatureTreeNameProjectionIndex();
    const incomingNameProjectionIndex = new FeatureTreeNameProjectionIndex();
    /** 当前完整刷新期间的名称更新，覆盖旧 Worker 快照中的标题。 */
    const pendingNameChanges = new Map<string, FeatureTreeNameChange>();
    /** 初次 Worker 快照尚未到达时使用的稳定空对象集合。 */
    const emptyNameProjectionObjects: readonly GeometryObject[] = [];
    /** 激活树与异步待提交树的诊断绑定分别持有，不将旧节点引用带入新投影。 */
    const mateDiagnosticIndex = new AssemblyMateDiagnosticPresentationIndex(presentationIndex);
    const incomingMateDiagnosticIndex = new AssemblyMateDiagnosticPresentationIndex(incomingPresentationIndex);
    /** 配合定义只更新挂载骨架，激活树与异步候选树不得共用节点引用。 */
    const mateTreeProjectionIndex = new AssemblyMateTreeProjectionIndex();
    const incomingMateTreeProjectionIndex = new AssemblyMateTreeProjectionIndex();
    /** 当前结构请求开始后的最新文档，覆盖 Worker 等待前捕获的配合投影。 */
    let pendingMateDocument: AssemblyMateDocumentModel | null = null;
    /** 期间是否出现定义变更；后续诊断只能更新最新文档，不能抹掉结构重放要求。 */
    let pendingMateDefinitionChanged = false;
    /** effect scope 释放后，迟到的 SDK 诊断回调不得重新持有旧 UI 节点。 */
    let isDisposed = false;
    /** 当前结构刷新开始后的展示更新；同一请求可能在 Worker 前后各提交一次。 */
    const pendingPresentationUpdates = new Map<string, GlbTreeNodePresentationUpdate>();
    /** 当前结构刷新开始后的可见性更新；以稳定节点 key 覆盖晚到投影中的旧状态。 */
    const pendingVisibilityChanges = new Map<string, boolean>();
    /** 运行时子树投影器与仅在完整刷新期间存活的有序差量快照。 */
    const runtimeSubtreeProjector = new FeatureTreeRuntimeSubtreeProjector();
    const pendingRuntimeSubtreeUpdates = new FeatureTreeRuntimeSubtreeReplay<FeatureTreeNodeMeta>();
    /** 隐藏对象集合。 */
    const hiddenObjectIds = new Set<string>();
    /** 当前由特征显隐状态写入隐藏对象集合的特征 ID。 */
    const hiddenFeatureIds = new Set<string>();
    /** 已删除对象集合。 */
    const deletedObjectIds = new Set<string>();
    /** 已压制草图集合。 */
    const suppressedSketchIds = new Set<string>();
    /** 草图生命周期映射。 */
    const sketchLifecycle = new Map<string, SketchLifecycleEntry>();
    /** 名称覆写表。 */
    const objectNameOverrides = new Map<string, string>();

    /** 草图序号计数器。 */
    let sketchSerial = 1;
    /** 最近一次 Worker 状态快照。 */
    let lastWorkerStateSummary: WorkerGeometryStateSummary | null = null;
    /** 当前仍允许提交设计树结果的刷新请求编号。 */
    let latestRefreshRequestId = 0;
    /**
     * GLB 设计树子树提供者。
     *
     * 两种工作模式（由提供者返回值的 `replaceTree` 决定）：
     * 1. Replace 模式（`replaceTree: true`，装配体 / 分组标签）：
     *    provider 拥有装配层级权威树。refreshTree() 跳过零件原生投影，以 `subtreeChildren`
     *    为基础合并装配体来源的正式基准对象与配合派生节点，并重建 `featureTreeNodeMap`；
     * 2. Merge 模式（默认，零件标签）：
     *    refreshTree() 先跑原生 CAD 投影，再把 `bodyChildren` 合进 `solid-bodies-folder`、
     *    `subtreeChildren` 追加到 part-root 的 children 末尾，`nodeMapEntries` merge 进元数据表。
     *
     * 返回 `null` 表示当前没有 GLB 模型需要注入，refreshTree() 只保留原生投影结果。
     */
    let glbTreeRefreshProvider:
        | ((context: GlbTreeRefreshProviderContext) => GlbMergeSubtreeProviderResult | null)
        | null = null;

    function registerGlbTreeRefreshProvider(
        provider: (context: GlbTreeRefreshProviderContext) => GlbMergeSubtreeProviderResult | null,
    ): void {
        glbTreeRefreshProvider = provider;
    }

    function unregisterGlbTreeRefreshProvider(): void {
        glbTreeRefreshProvider = null;
        // 文档卸载后，旧 Worker 请求不能继续提交；同时解除所有派生索引引用。
        latestRefreshRequestId += 1;
        presentationIndex.clear();
        incomingPresentationIndex.clear();
        pendingPresentationUpdates.clear();
        pendingNameChanges.clear();
        nameProjectionIndex.clear();
        incomingNameProjectionIndex.clear();
        mateDiagnosticIndex.clear();
        incomingMateDiagnosticIndex.clear();
        mateTreeProjectionIndex.clear();
        incomingMateTreeProjectionIndex.clear();
        pendingMateDocument = null;
        pendingMateDefinitionChanged = false;
        pendingVisibilityChanges.clear();
        pendingRuntimeSubtreeUpdates.clear();
    }

    onScopeDispose(() => {
        isDisposed = true;
        unregisterGlbTreeRefreshProvider();
    });

    /**
     * 对当前树或待提交树应用同一展示变更，节点和元数据必须成对更新。
     * @param tree 要更新的纯 UI 树。
     * @param nodeMap 该树对应的元数据表。
     * @param index 该树的稳定节点索引。
     * @param updates SDK 层级变更对应的展示字段。
     */
    function applyPresentationUpdates(
        tree: readonly TreeOption[],
        nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
        index: FeatureTreePresentationIndex,
        updates: Iterable<GlbTreeNodePresentationUpdate>,
    ): void {
        for (const update of updates) {
            const node = index.resolve(tree, update.key);
            if (node) node.label = update.displayName;
            const meta = nodeMap.get(update.key);
            if (meta) {
                meta.displayName = update.displayName;
                meta.assemblyFixationState = update.fixationState;
                meta.canFixOrFloat = update.canFixOrFloat;
            }
        }
    }

    /**
     * 更新激活标签的 GLB 展示字段，不重新投影结构、请求 Worker 或重置选择。
     * @param tabId 变更来源标签；后台标签只更新其权威层级，不污染激活树。
     * @param updates 已由完整构树的同一格式规则生成的增量字段。
     */
    function applyGlbNodePresentationUpdates(tabId: string, updates: readonly GlbTreeNodePresentationUpdate[]): void {
        if (tabId !== activeTreeTabId.value || updates.length === 0) return;
        for (const update of updates) pendingPresentationUpdates.set(update.key, update);
        applyPresentationUpdates(featureTreeData.value, featureTreeNodeMap, presentationIndex, updates);
    }

    /**
     * 对一份树投影应用节点可见性字段更新。
     *
     * @param tree 要更新的纯 UI 树。
     * @param nodeMap 与树配套的节点元数据映射。
     * @param index 当前树的稳定节点索引。
     * @param changes 稳定节点 key 到隐藏状态的变更集合。
     */
    function applyVisibilityChanges(
        tree: readonly TreeOption[],
        nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
        index: FeatureTreePresentationIndex,
        changes: Iterable<readonly [string, boolean]>,
    ): void {
        for (const [key, hidden] of changes) {
            const node = index.resolve(tree, key);
            if (node) node.statusHidden = hidden;
            const meta = nodeMap.get(key);
            if (meta) meta.statusHidden = hidden;
        }
    }

    /**
     * 更新当前激活设计树中已存在节点的可见性，不重新请求 Worker 或投影结构。
     *
     * 设计说明：
     * 1. 只有节点与元数据同时存在时才接受增量，避免 UI 树与动作语义错位；
     * 2. 结构刷新等待期间保存最新值，晚到的旧投影在提交前会被同一变更覆盖；
     * 3. 后台标签和已释放 store 不得污染当前激活树。
     *
     * @param tabId 变更所属标签。
     * @param key 与完整投影一致的稳定节点 key。
     * @param hidden 节点是否隐藏。
     * @returns 增量是否已应用；返回 false 时调用方应回退完整刷新。
     */
    function applyNodeVisibilityChange(tabId: string | null, key: string, hidden: boolean): boolean {
        if (isDisposed || tabId !== activeTreeTabId.value) return false;
        const node = presentationIndex.resolve(featureTreeData.value, key);
        const meta = featureTreeNodeMap.get(key);
        if (!node || !meta) return false;
        node.statusHidden = hidden;
        meta.statusHidden = hidden;
        pendingVisibilityChanges.set(key, hidden);
        return true;
    }

    /**
     * 在当前激活树的稳定父节点下替换运行时子树。
     *
     * 设计说明：
     * 1. 领域层必须先生成完整的新子树与元数据，本入口不解释 GLB 或阵列协议；
     * 2. 更新保留父节点和全部无关节点引用，只在真实结构变化时递增投影修订；
     * 3. 当前完整刷新等待期间保存最终更新，避免晚到旧投影恢复已删除的运行时子树。
     *
     * @param tabId 更新所属标签。
     * @param update 运行时子树更新。
     * @returns 稳定父节点存在并已应用时返回 true。
     */
    function applyGlbRuntimeSubtreeUpdate(
        tabId: string | null,
        update: GlbRuntimeSubtreeUpdate<FeatureTreeNodeMeta>,
    ): boolean {
        if (isDisposed || tabId !== activeTreeTabId.value) return false;
        const applied = runtimeSubtreeProjector.apply(
            featureTreeData.value,
            featureTreeNodeMap,
            presentationIndex,
            update,
        );
        if (!applied) return false;

        pendingRuntimeSubtreeUpdates.record(update);
        const retainedSelectedKeys = selectedKeys.value.filter(key => featureTreeNodeMap.has(key));
        if (retainedSelectedKeys.length !== selectedKeys.value.length) {
            selectedKeys.value = retainedSelectedKeys;
            if (retainedSelectedKeys.length === 0) selectedObject.value = null;
        }
        nameProjectionIndex.clear();
        mateDiagnosticIndex.clear();
        mateTreeProjectionIndex.clear();
        featureTreeRevision.value += 1;
        return true;
    }

    /**
     * 对已完成业务重命名的原生对象应用标题增量，不触发 Worker 或 GLB 全树投影。
     * @param tabId 名称更新所属标签；后台标签不能修改当前树。
     * @param change 稳定身份及新名称。
     * @returns 支持并已应用时返回 true，未接入的外部层级保持原完整刷新路径。
     */
    function applyNodeNameChange(tabId: string | null, change: FeatureTreeNameChange): boolean {
        if (isDisposed || tabId !== activeTreeTabId.value || !change.name.trim()) return false;
        const applied = nameProjectionIndex.apply(featureTreeData.value, featureTreeNodeMap,
            lastWorkerStateSummary?.objects ?? emptyNameProjectionObjects, change, objectNameOverrides);
        if (applied) pendingNameChanges.set(change.key, change);
        return applied;
    }

    /**
     * 仅同步已存在 Mate 节点的诊断；定义增删由独立的配合局部投影处理。
     * @param tabId 诊断所属标签，后台标签不得污染激活树。
     * @param document SDK 发布的最新配合文档。
     */
    function applyAssemblyMateDiagnostics(tabId: string | null, document: AssemblyMateDocumentModel): void {
        if (isDisposed || tabId !== activeTreeTabId.value) return;
        pendingMateDocument = document;
        mateDiagnosticIndex.apply(featureTreeData.value, featureTreeNodeMap, document);
    }

    /**
     * 更新配合定义的全部出现项，不请求模型 provider、Worker 或重建无关实体节点。
     * @param tabId 配合文档所属标签。
     * @param document SDK 已提交/恢复的权威配合文档；本入口不改变历史和模型主状态。
     */
    function applyAssemblyMateDocument(tabId: string | null, document: AssemblyMateDocumentModel): void {
        if (isDisposed || tabId !== activeTreeTabId.value) return;
        pendingMateDocument = document;
        pendingMateDefinitionChanged = true;
        const changed = mateTreeProjectionIndex.apply(featureTreeData.value, featureTreeNodeMap, document);
        // 配合引用增删会使稳定 key 索引和诊断元数据绑定失效，但模型挂载骨架仍可复用。
        mateDiagnosticIndex.clear();
        if (changed) {
            presentationIndex.clear();
            featureTreeRevision.value += 1;
        }
    }

    /**
     * 将外部模型树节点的导入边界引用归一为统一模型体身份。
     *
     * 正常链路直接消费 cad-core 从 BRep 注册结果透传的 geometryObjectId；只有旧包缺少该字段时，
     * 才允许在这一处通过 bodyBindingId 兼容解析。身份命中后，后续去重、消费投影和 Undo/Redo
     * 恢复全部只使用 modelBodyRevisionId。
     *
     * @param glbSubtree 外部模型树投影片段。
     * @returns 已写入统一模型体身份的投影片段。
     */
    function bindExternalTreeModelBodyIdentities(
        glbSubtree: GlbMergeSubtreeProviderResult,
    ): GlbMergeSubtreeProviderResult {
        const viewer = getViewer();
        if (!viewer) {
            return glbSubtree;
        }
        let hasIdentityChange = false;
        const nodeMapEntries = new Map<string, FeatureTreeNodeMeta>();
        glbSubtree.nodeMapEntries.forEach((meta, key) => {
            if (meta.objectType !== 'body') {
                nodeMapEntries.set(key, meta);
                return;
            }
            const authoritativeRevisionId = meta.modelBodyRevisionId?.trim() || '';
            const boundaryReferenceId = authoritativeRevisionId
                || meta.bodyBindingId?.trim()
                || meta.glbBodyBindingId?.trim()
                || '';
            const identity = boundaryReferenceId
                ? viewer.resolveModelBodySceneIdentity(boundaryReferenceId)
                : null;
            if (!identity) {
                nodeMapEntries.set(key, authoritativeRevisionId && !meta.modelBodyId
                    ? {
                        ...meta,
                        modelBodyId: authoritativeRevisionId,
                    }
                    : meta);
                return;
            }
            if (
                meta.modelBodyId !== identity.modelBodyId
                || meta.modelBodyRevisionId !== identity.modelBodyRevisionId
            ) {
                hasIdentityChange = true;
            }
            nodeMapEntries.set(key, {
                ...meta,
                modelBodyId: identity.modelBodyId,
                modelBodyRevisionId: identity.modelBodyRevisionId,
            });
        });

        return hasIdentityChange
            ? {...glbSubtree, nodeMapEntries}
            : glbSubtree;
    }

    /**
     * 按 Worker 权威模型体修订状态过滤外部模型设计树投影片段。
     *
     * @param glbSubtree 外部模型树投影片段。
     * @param objects Worker 当前活动对象。
     * @returns 已移除失活旧修订节点的投影片段。
     */
    function applyModelBodyStateToGlbSubtree(
        glbSubtree: GlbMergeSubtreeProviderResult,
        objects: readonly GeometryObject[],
    ): GlbMergeSubtreeProviderResult {
        const resolvedSubtree = bindExternalTreeModelBodyIdentities(glbSubtree);
        const projectionState = resolveModelBodyProjectionState(objects);
        if (projectionState.consumedRevisionIds.size === 0) {
            return resolvedSubtree;
        }

        const filtered = filterModelBodyTreeProjection({
            bodyChildren: resolvedSubtree.bodyChildren,
            subtreeChildren: resolvedSubtree.subtreeChildren,
            nodeMapEntries: new Map(resolvedSubtree.nodeMapEntries),
            projectionState,
        });

        return {
            ...resolvedSubtree,
            bodyChildren: filtered.bodyChildren as TreeOption[],
            subtreeChildren: filtered.subtreeChildren as TreeOption[],
            nodeMapEntries: filtered.nodeMapEntries as Map<string, FeatureTreeNodeMeta>,
        };
    }

    /**
     * 创建一次显式绑定目标 tab 的刷新请求。
     *
     * 设计说明：
     * 1. 当前 store 仍只维护一份“激活设计树”，所以每次刷新都必须声明要替换哪一个 tab 的树；
     * 2. 通过递增 requestId，可阻止旧异步请求在较晚返回时覆盖新结果；
     * 3. `activeTreeTabId` 与 requestId 成对更新，保证“树内容”和“树归属”同步切换。
     *
     * @param tabId 目标 tab ID。
     * @returns 本次刷新的请求编号。
     */
    function beginTreeRefresh(tabId: string | null): number {
        activeTreeTabId.value = tabId;
        latestRefreshRequestId += 1;
        // 新请求会重新读取权威 hierarchy；旧请求已失去提交资格，不得把旧增量带入新结构。
        pendingPresentationUpdates.clear();
        pendingVisibilityChanges.clear();
        pendingRuntimeSubtreeUpdates.begin(latestRefreshRequestId);
        pendingNameChanges.clear();
        pendingMateDocument = null;
        pendingMateDefinitionChanged = false;
        return latestRefreshRequestId;
    }

    /**
     * 判断当前刷新请求是否仍然具备提交资格。
     *
     * @param requestId 本次刷新的请求编号。
     * @param tabId 目标 tab ID。
     * @returns 若仍是最新的目标 tab 刷新，则返回 `true`。
     */
    function canCommitTreeRefresh(requestId: number, tabId: string | null): boolean {
        return latestRefreshRequestId === requestId && activeTreeTabId.value === tabId;
    }

    /**
     * 原子提交当前激活设计树投影。
     *
     * 设计说明：
     * 1. 树节点数组与节点元数据映射必须成对替换，避免 hover/click 读到错位的 nodeMap；
     * 2. 这里只有 store 能写入 active projection，统一收口后更容易排查时序问题；
     * 3. `nextTreeData` 会浅拷贝一份，避免上游复用数组引用造成后续意外串写。
     *
     * @param nextTreeData 设计树节点数组。
     * @param nextNodeMap 下一版节点元数据映射。
     */
    function commitActiveTreeProjection(
        nextTreeData: readonly TreeOption[],
        nextNodeMap: Map<string, FeatureTreeNodeMeta>,
        objects: readonly GeometryObject[] = lastWorkerStateSummary?.objects ?? emptyNameProjectionObjects,
    ): void {
        // 先按真实顺序恢复结构，再重放字段更新，避免新插入节点的最新固定状态被结构快照覆盖。
        pendingRuntimeSubtreeUpdates.replay(nextTreeData, nextNodeMap, incomingPresentationIndex, runtimeSubtreeProjector);
        applyPresentationUpdates(nextTreeData, nextNodeMap, incomingPresentationIndex, pendingPresentationUpdates.values());
        // 显示/隐藏动作已经提交主状态时，晚到结构投影必须采用该动作的最终可见性。
        applyVisibilityChanges(nextTreeData, nextNodeMap, incomingPresentationIndex, pendingVisibilityChanges.entries());
        pendingNameChanges.forEach(change => incomingNameProjectionIndex.apply(nextTreeData, nextNodeMap, objects, change, objectNameOverrides));
        incomingNameProjectionIndex.clear();
        nameProjectionIndex.clear();
        if (pendingMateDocument) {
            if (pendingMateDefinitionChanged) {
                incomingMateTreeProjectionIndex.apply(nextTreeData, nextNodeMap, pendingMateDocument);
            } else {
                incomingMateDiagnosticIndex.apply(nextTreeData, nextNodeMap, pendingMateDocument);
            }
        }
        incomingMateTreeProjectionIndex.clear();
        mateTreeProjectionIndex.clear();
        incomingMateDiagnosticIndex.clear();
        mateDiagnosticIndex.clear();
        incomingPresentationIndex.clear();
        presentationIndex.clear();
        featureTreeNodeMap.clear();
        nextNodeMap.forEach((meta, key) => {
            featureTreeNodeMap.set(key, meta);
        });
        featureTreeData.value = [...nextTreeData];
        featureTreeRevision.value += 1;
    }

    /**
     * 按刷新请求原子提交当前激活树快照。
     *
     * 设计说明：
     * 1. `featureTreeData` / `featureTreeNodeMap` / `lastWorkerStateSummary`
     *    必须共享同一份“最新 refresh”所有权，避免旧请求晚返回时只覆盖摘要、不覆盖树；
     * 2. 调用点只需准备好完整快照，然后由这里统一做资格校验，减少遗漏；
     * 3. 返回值显式告知 caller 是否真正提交，便于后续扩展调试日志或指标。
     *
     * @param requestId 本次刷新的请求编号。
     * @param tabId 本次刷新的目标标签。
     * @param nextTreeData 下一版设计树节点数组。
     * @param nextNodeMap 下一版节点元数据映射。
     * @param stateSummary 与本次树快照对应的 Worker 状态摘要。
     * @returns 若本次刷新仍具备提交资格，则返回 `true`。
     */
    function commitTreeRefreshSnapshot(
        requestId: number,
        tabId: string | null,
        nextTreeData: readonly TreeOption[],
        nextNodeMap: Map<string, FeatureTreeNodeMeta>,
        stateSummary: WorkerGeometryStateSummary,
    ): boolean {
        if (!canCommitTreeRefresh(requestId, tabId)) {
            return false;
        }
        commitActiveTreeProjection(nextTreeData, nextNodeMap, stateSummary.objects);
        lastWorkerStateSummary = stateSummary;
        return true;
    }

    /**
     * 按刷新请求提交 Worker 状态摘要。
     *
     * 设计说明：
     * 1. `replaceTree` 路径需要保留“先换树、后等待 worker state”的即时反馈体验；
     * 2. 旧实现的问题不在于树太早提交，而在于晚返回的旧 summary 没有 request guard；
     * 3. 因此这里单独收口 summary 提交，让 replaceTree 路径既不回退体验，也不再污染新 tab。
     *
     * @param requestId 本次刷新的请求编号。
     * @param tabId 本次刷新的目标标签。
     * @param stateSummary 与本次刷新对应的 Worker 状态摘要。
     * @returns 若本次摘要仍具备提交资格，则返回 `true`。
     */
    function commitTreeRefreshStateSummary(
        requestId: number,
        tabId: string | null,
        stateSummary: WorkerGeometryStateSummary,
    ): boolean {
        if (!canCommitTreeRefresh(requestId, tabId)) {
            return false;
        }
        lastWorkerStateSummary = stateSummary;
        return true;
    }

    /**
     * 读取 cad-core Mate 文档并构建设计树投影。
     *
     * @param sourceNodeMap 当前 imported-model 设计树元数据。
     * @returns 当前装配体配合设计树投影；没有配合时返回空投影。
     */
    function resolveAssemblyMateTreeProjection(
        sourceNodeMap?: ReadonlyMap<string, FeatureTreeNodeMeta>,
    ) {
        return buildAssemblyMateTreeProjection(
            getViewer()?.assemblyMateController?.getDocument?.() ?? null,
            sourceNodeMap,
        );
    }

    /**
     * 当前选中对象的显示名称。
     */
    const selectedObjectName = computed(() => getObjectDisplayName(selectedObject.value));

    /**
     * 初始化设计树外部依赖。
     * @param providers 外部依赖提供者。
     */
    function initProviders(providers: FeatureTreeProviders): void {
        messageProvider = providers.message;
        dialogProvider = providers.dialog;
        enterSketchEditingOnDatumPlaneProvider = providers.enterSketchEditingOnDatumPlane;
        handleSketchCommandProvider = providers.handleSketchCommand;
        handleFeatureEditProvider = providers.handleFeatureEdit ?? null;
        //------ 基准面编辑特征 开始------
        handleReferencePlaneEditProvider = providers.handleReferencePlaneEdit ?? null;
        //------ 基准面编辑特征 结束------
        //------ 基准轴编辑特征 开始------
        handleReferenceAxisEditProvider = providers.handleReferenceAxisEdit ?? null;
        //------ 基准轴编辑特征 结束------
        //------ 基准点编辑特征 开始------
        handleReferencePointEditProvider = providers.handleReferencePointEdit ?? null;
        //------ 基准点编辑特征 结束------
        handleFeatureEditSketchProvider = providers.handleFeatureEditSketch ?? null;
        handleSketchLifecycleActionProvider = providers.handleSketchLifecycleAction ?? null;
        //------ 基准对象删除历史 开始------
        handleDatumReferenceDeleteActionProvider = providers.handleDatumReferenceDeleteAction ?? null;
        //------ 基准对象删除历史 结束------
        handleFeatureDeleteActionProvider = providers.handleFeatureDeleteAction ?? null;
        handleAssemblyComponentDeleteActionProvider = providers.handleAssemblyComponentDeleteAction ?? null;
        handleFeatureVisibilityActionProvider = providers.handleFeatureVisibilityAction ?? null;
        handleFeatureRollbackActionProvider = providers.handleFeatureRollbackAction ?? null;
        handleAssemblyComponentFixationChangeProvider = providers.handleAssemblyComponentFixationChange ?? null;
        handleAssemblyMateEditProvider = providers.handleAssemblyMateEdit ?? null;
        //------ 线性零部件阵列节点删除 开始------
        handleLinearComponentPatternDeleteProvider = providers.handleLinearComponentPatternDelete ?? null;
        handleLinearComponentPatternEditProvider = providers.handleLinearComponentPatternEdit ?? null;
        //------ 线性零部件阵列节点删除 结束------
    }

    /**
     * 读取当前几何状态摘要。
     * @returns Viewer 同步后的状态摘要。
     */
    async function getStateSummary(): Promise<WorkerGeometryStateSummary> {
        const viewer = getViewer();
        if (viewer?.getFeatureTreeStateSummary) {
            return viewer.getFeatureTreeStateSummary();
        }
        throw new Error('Viewer 未提供设计树专用状态摘要，无法安全刷新特征树。');
    }

    /**
     * 读取退回控制棒投影状态。
     *
     * 设计说明：
     * 1. 退回控制棒完整时间线由 cad-core Viewer 维护，store 不自行推断；
     * 2. 投影状态只包含 cursor 和灰显特征 ID，避免把完整命令历史放进 Vue 响应式对象；
     * 3. 未接入 Viewer 门面时返回 null，保持旧项目树投影兼容。
     *
     * @returns 退回控制棒投影状态。
     */
    /**
     * 读取设计树投影原子快照。
     *
     * 设计说明：
     * 1. 回退控制棒移动后，Worker history 是激活前缀，完整 FeatureManager history 在 cad-core controller 内；
     * 2. 设计树必须一次性消费 state 与 rollbackState，不能由 web-client 分两次读取后自行拼装；
     * 3. 这样可以避免简单拉伸回退时出现“特征消失、源草图释放为顶层”的状态错配。
     *
     * @returns 设计树投影快照。
     */
    async function getProjectionSnapshot(): Promise<FeatureTreeProjectionSnapshot> {
        const viewer = getViewer();
        if (viewer?.getFeatureTreeProjectionSnapshot) {
            return viewer.getFeatureTreeProjectionSnapshot();
        }
        throw new Error('Viewer 未提供设计树原子投影快照，无法安全刷新特征树。');
    }

    /**
     * 通过对象 ID 获取场景对象。
     * @param objectId 对象 ID。
     * @returns 场景对象；未命中时返回 `null`。
     */
    function getSceneObjectById(objectId: string) {
        return getViewer()?.getSceneObjectById(objectId) ?? null;
    }

    /**
     * 收集当前受管草图摘要。
     * @returns 草图摘要列表。
     */
    function getManagedSketches() {
        const sketchSummaries = getViewer()?.listSketchGroupSummaries?.() ?? [];
        return collectManagedSketches(sketchSummaries, deletedObjectIds, suppressedSketchIds);
    }

    //------ 特征消费草图自动隐藏 开始------
    /**
     * 从 Viewer 草图组显示态同步设计树使用的隐藏集合。
     *
     * 设计说明：
     * 1. 设计树草图隐藏状态必须以 cad-core 的 `SketchDisplayState` 为准；
     * 2. 每次刷新前先回灌隐藏集合，避免出现“图形区已隐藏但树未标记隐藏”的状态分裂；
     * 3. 仅覆盖当前受管草图 ID，不干扰未来可能接入的其它对象隐藏语义。
     */
    function syncHiddenSketchIdsFromViewer(): void {
        const sketchSummaries = getViewer()?.listSketchGroupSummaries?.() ?? [];
        const managedSketchIds = new Set(sketchSummaries.map((summary) => summary.id));

        Array.from(hiddenObjectIds).forEach((objectId) => {
            if (managedSketchIds.has(objectId)) {
                hiddenObjectIds.delete(objectId);
            }
        });

        sketchSummaries.forEach((summary) => {
            if (summary.displayState === SketchDisplayState.Hidden) {
                hiddenObjectIds.add(summary.id);
            }
        });
    }

    /**
     * 从 Viewer 结果体显隐状态同步设计树使用的特征隐藏集合。
     *
     * 设计说明：
     * 1. 特征隐藏属于 Feature 语义，不能由 FeatureTree.vue 直接读取 Object3D.visible；
     * 2. Viewer 负责把运行时结果体显隐归一成 featureId 集合，store 只把它合入投影输入；
     * 3. 每次同步先移除上一轮 feature 隐藏项，避免删除特征后留下过期 ID。
     *
     * @param history 当前 Worker 命令历史。
     */
    function syncHiddenFeatureIdsFromViewer(history: readonly CadCommand[]): void {
        hiddenFeatureIds.forEach((featureId) => {
            hiddenObjectIds.delete(featureId);
        });
        hiddenFeatureIds.clear();

        const managedFeatureIds = new Set(history.map((command) => command.id));
        const nextHiddenFeatureIds = getViewer()?.getHiddenFeatureIds?.() ?? [];
        nextHiddenFeatureIds.forEach((featureId) => {
            if (!managedFeatureIds.has(featureId)) {
                return;
            }
            hiddenFeatureIds.add(featureId);
            hiddenObjectIds.add(featureId);
        });
    }
    //------ 特征消费草图自动隐藏 结束------

    //------ 特征树基准对象直连读取 开始------
    /**
     * 直接从 Viewer 挂载的基准对象管理器读取当前正式参考对象。
     *
     * 设计说明：
     * 1. 特征树只需要 `id / name / isVisible() / canEditReferencePlane` 这一层稳定摘要契约；
     * 2. 这里直接依赖 `viewer.datumPlaneManager / datumAxisManager / datumPointManager`，
     *    避免再要求 Viewer 为特征树额外包一层转发方法；
     * 3. 统一收口到单个函数后，刷新树与兼容旧接口构建都能复用同一数据来源，避免重复读取逻辑。
     * 4. 装配体 `replaceTree` 必须传入 `Assembly` 创建来源，只读取装配体命令创建的对象，禁止混入零件态宿主基准对象。
     *
     * @param creationContext 可选的正式基准对象创建来源过滤条件。
     * @returns 当前基准面、基准轴、基准点摘要集合。
     */
    function getDatumReferenceSummaries(creationContext?: ReferenceCreationContext): {
        datumPlanes: FeatureTreeDatumPlaneSummary[];
        datumAxes: FeatureTreeDatumAxisSummary[];
        datumPoints: FeatureTreeDatumPointSummary[];
    } {
        const viewer = getViewer();
        const datumPlanes = (viewer?.datumPlaneManager?.getAllPersistentPlanes?.() ?? [])
            .filter((plane) => creationContext === undefined || plane.creationContext === creationContext);
        const datumAxes = (viewer?.datumAxisManager?.getAllAxes?.() ?? [])
            .filter((axis) => creationContext === undefined || axis.creationContext === creationContext);
        const datumPoints = (viewer?.datumPointManager?.getAllPoints?.() ?? [])
            .filter((point) => creationContext === undefined || point.creationContext === creationContext);

        return {
            //------ 基准面编辑特征 开始------
            // 基准面对象本身符合摘要契约；下面的 map 仅补充编辑特征能力，避免投影层直接读取运行时对象。
            datumPlanes: datumPlanes.map((plane) => ({
                id: plane.id,
                name: plane.name,
                //------ 默认基准面稳定身份 开始------
                defaultPlaneKind: plane.defaultPlaneKind,
                //------ 默认基准面稳定身份 结束------
                canEditReferencePlane: Boolean(plane.getCalculationSource()),
                createdAt: viewer?.datumPlaneManager?.getUserCreatedPlaneCreatedAt(plane.id),
                isVisible: () => plane.isVisible(),
            })) ?? [],
            //------ 基准面编辑特征 结束------
            datumAxes: datumAxes.map((axis) => {
                return {
                    // 基准轴必须使用稳定业务 ID，禁止把 Three.js Object3D.id 暴露给设计树动作链路。
                    id: axis.datumAxisId,
                    name: axis.name,
                    // 基准轴是否可编辑由控制器中的可复算记录决定。
                    canEditReferenceAxis: viewer?.referenceAxisController?.canEditFeatureByAxisId?.(axis.datumAxisId) === true,
                    createdAt: viewer?.datumAxisManager?.getUserCreatedAxisCreatedAt(axis.datumAxisId),
                    isVisible: () => axis.isVisible(),
                };
            }) ?? [],
            datumPoints: datumPoints.map((point) => {
                return {
                    // 基准点需要额外保留原点语义，用于投影层决定是否展示默认原点。
                    id: point.datumPointId,
                    name: point.name,
                    // 基准点是否可编辑由控制器中的可复算记录决定。
                    canEditReferencePoint: viewer?.referencePointController?.canEditFeatureByPointId?.(point.datumPointId) === true,
                    isOrigin: point.isOrigin,
                    createdAt: viewer?.datumPointManager?.getUserCreatedPointCreatedAt(point.datumPointId),
                    isVisible: () => point.isVisible(),
                };
            }) ?? [],
        };
    }
    //------ 特征树基准对象直连读取 结束------

    /**
     * 通过对象 ID 读取设计树对象摘要。
     * @param objectId 对象 ID。
     * @returns 设计树对象摘要；未命中时返回 `null`。
     */
    async function getObjectById(objectId: string): Promise<FeatureTreeObjectSummary | null> {
        const state = lastWorkerStateSummary ?? await getStateSummary();
        lastWorkerStateSummary = state;
        return resolveFeatureTreeObjectSummary({
            state,
            objectId,
            sceneSketches: getManagedSketches(),
            hiddenObjectIds,
            suppressedSketchIds,
            objectNameOverrides,
        });
    }

    /**
     * 同步草图序号，防止新建草图名称与现有草图冲突。
     */
    function syncSketchSerial(): void {
        sketchSerial = syncSketchSerialByManagedSketches(sketchSerial, getManagedSketches());
    }

    /**
     * 弹出重命名对话框。
     * @param title 对话框标题。
     * @param defaultValue 默认值。
     * @returns 新名称；取消时返回 `null`。
     */
    function requestRename(title: string, defaultValue: string): Promise<string | null> {
        const requestedTabId = activeTreeTabId.value;
        return new Promise((resolve) => {
            const inputValue = ref(defaultValue);
            let resolved = false;

            /**
             * 对话框可能从多个出口结束。
             * 这里统一收口，避免重复 resolve 导致状态不一致。
             */
            const finish = (value: string | null): void => {
                if (resolved) {
                    return;
                }
                resolved = true;
                // 对话框晚于文档切换/卸载结束时按取消处理，不能把旧标签输入写进新模型。
                resolve(isDisposed || requestedTabId !== activeTreeTabId.value ? null : value);
            };

            dialogProvider?.create({
                title,
                content: () => h(NInput, {
                    value: inputValue.value,
                    placeholder: '请输入名称',
                    autofocus: true,
                    onUpdateValue: (value: string) => {
                        inputValue.value = value;
                    },
                }),
                positiveText: '确定',
                negativeText: '取消',
                onPositiveClick: () => {
                    const trimmed = inputValue.value.trim();
                    if (!trimmed) {
                        messageProvider?.warning('名称不能为空');
                        return false;
                    }
                    finish(trimmed);
                    return true;
                },
                onNegativeClick: () => {
                    finish(null);
                },
                onClose: () => {
                    finish(null);
                },
            });
        });
    }

    /**
     * 根据当前状态刷新设计树。
     *
     * 合并策略：
     * 1. 先询问 GLB 提供者；如果返回 `replaceTree: true`（装配体 / 分组标签），
     *    GLB 子树负责装配层级，宿主 Viewer 中仅 `Assembly` 来源的正式基准对象作为派生节点注入根节点；
     * 2. 否则运行原生 CAD 投影，保证特征/实体/基准节点完整；
     * 3. 如果 GLB 提供者同时返回合并用的 `bodyChildren` / `subtreeChildren`（零件标签），
     *    把它们追加到 part-root / solid-bodies-folder；
     * 4. 返回状态摘要给上层 UI 展示。
     *
     * @returns 当前对象与命令数量摘要。
     */
    async function refreshTree(tabId: string | null = activeTreeTabId.value ?? partTabsStore.activeTabId): Promise<{ objectCount: number; commandCount: number }> {
        const targetTabId = tabId ?? null;
        const refreshRequestId = beginTreeRefresh(targetTabId);
        try {
            return await buildTreeRefreshSnapshot(targetTabId, refreshRequestId);
        } finally {
            // 成功、拒绝或被新请求抢占都结束本请求记录；迟到旧请求不能清除新请求的差量。
            pendingRuntimeSubtreeUpdates.end(refreshRequestId);
        }
    }

    /**
     * 在已有刷新请求下构建并提交模型投影。
     *
     * @param targetTabId 当前请求的目标标签。
     * @param refreshRequestId 提交资格令牌。
     * @returns Worker 统计摘要；结构重放的生命周期由外层 refreshTree 统一释放。
     */
    async function buildTreeRefreshSnapshot(targetTabId: string | null, refreshRequestId: number): Promise<{ objectCount: number; commandCount: number }> {
        syncHiddenSketchIdsFromViewer();
        const managedSketches = getManagedSketches();
        const partLabel = getPartLabelByTabId(targetTabId);

        // 先同步构建 GLB 子树，再决定是否需要跑原生投影。
        // 对装配体 / 分组标签来说，provider 已经拥有整棵权威树；
        // 若把这一步放到 `await getStateSummary()` 之后，调用方在 Worker 往返期间会看到
        // 一个尚未更新的旧树或空树，进而在"切 tab 后立刻插入"这类场景里触发设计树竞态。
        const glbSubtree = glbTreeRefreshProvider?.({
            sceneSketches: managedSketches,
            tabId: targetTabId,
            partLabel,
        }) ?? null;

        if (glbSubtree?.replaceTree === true) {
            const {datumPlanes, datumAxes, datumPoints} = getDatumReferenceSummaries(
                ReferenceCreationContext.Assembly,
            );
            const assemblyDatumProjection = buildAssemblyDatumReferenceTreeProjection({
                datumPlanes,
                datumAxes,
                datumPoints,
            });
            const nextNodeMap = new Map<string, FeatureTreeNodeMeta>(glbSubtree.nodeMapEntries);
            assemblyDatumProjection.featureTreeNodeMap.forEach((meta, key) => {
                nextNodeMap.set(key, meta);
            });
            const assemblyMateProjection = resolveAssemblyMateTreeProjection(nextNodeMap);
            assemblyMateProjection.featureTreeNodeMap.forEach((meta, key) => {
                nextNodeMap.set(key, meta);
            });
            const treeDataWithAssemblyDatums = mergeAssemblyDatumReferenceTree(
                glbSubtree.subtreeChildren,
                assemblyDatumProjection.treeNodes,
            );
            const nextTreeData = mergeAssemblyMateFoldersIntoTree(
                treeDataWithAssemblyDatums,
                assemblyMateProjection,
                nextNodeMap,
            );
            if (canCommitTreeRefresh(refreshRequestId, targetTabId)) {
                commitActiveTreeProjection(nextTreeData, nextNodeMap);
            }

            const {state} = await getProjectionSnapshot();
            const resolvedGlbSubtree = applyModelBodyStateToGlbSubtree(glbSubtree, state.objects);
            if (resolvedGlbSubtree !== glbSubtree) {
                const resolvedNodeMap = new Map<string, FeatureTreeNodeMeta>(resolvedGlbSubtree.nodeMapEntries);
                assemblyDatumProjection.featureTreeNodeMap.forEach((meta, key) => {
                    resolvedNodeMap.set(key, meta);
                });
                assemblyMateProjection.featureTreeNodeMap.forEach((meta, key) => {
                    resolvedNodeMap.set(key, meta);
                });
                const resolvedTreeDataWithAssemblyDatums = mergeAssemblyDatumReferenceTree(
                    resolvedGlbSubtree.subtreeChildren,
                    assemblyDatumProjection.treeNodes,
                );
                const resolvedTreeData = mergeAssemblyMateFoldersIntoTree(
                    resolvedTreeDataWithAssemblyDatums,
                    assemblyMateProjection,
                    resolvedNodeMap,
                );
                if (canCommitTreeRefresh(refreshRequestId, targetTabId)) {
                    commitActiveTreeProjection(resolvedTreeData, resolvedNodeMap, state.objects);
                }
            }
            commitTreeRefreshStateSummary(
                refreshRequestId,
                targetTabId,
                state,
            );
            return {
                objectCount: state.objectCount,
                commandCount: state.commandCount,
            };
        }

        const {state, rollbackState} = await getProjectionSnapshot();
        syncHiddenFeatureIdsFromViewer(state.history);
        const resolvedGlbSubtree = glbSubtree
            ? applyModelBodyStateToGlbSubtree(glbSubtree, state.objects)
            : null;

        const {datumPlanes, datumAxes, datumPoints} = getDatumReferenceSummaries();

        /**
         * 树投影构建已经下沉到纯函数中。
         * store 只负责把投影结果写回响应式状态与共享元数据缓存。
         */
        const projection = buildFeatureTreeProjection({
            objects: state.objects,
            history: state.history,
            datumPlanes,
            datumAxes,
            datumPoints,
            sceneSketches: managedSketches,
            hiddenObjectIds,
            suppressedSketchIds,
            objectNameOverrides,
            partLabel,
            rollbackState,
        });

        const projectedRoot = projection.featureTreeData[0];
        const projectedRootChildren = projectedRoot?.key === 'part-root' && Array.isArray(projectedRoot.children)
            ? (projectedRoot.children as TreeOption[])
            : [];
        const projectedBodyFolder = projectedRootChildren.find((node) => node.key === 'solid-bodies-folder');
        const projectedBodyChildren = projectedBodyFolder && Array.isArray(projectedBodyFolder.children)
            ? (projectedBodyFolder.children as TreeOption[])
            : [];
        /**
         * GLB 实体节点进入最终实体文件夹前，先与同批 Worker 投影按精确修订 ID 对账。
         * 后续去重只删除重复的 Worker 视图节点，不再承担身份或显示所有权修正。
         */
        const authoritativeGlbSubtree = resolvedGlbSubtree
            ? reconcileExternalBodyProjectionWithWorkerBodies(
                resolvedGlbSubtree,
                projectedBodyChildren,
                projection.featureTreeNodeMap,
            )
            : null;

        const hasBodyMerge = !!authoritativeGlbSubtree && authoritativeGlbSubtree.bodyChildren.length > 0;
        const hasSubtreeMerge = !!authoritativeGlbSubtree && authoritativeGlbSubtree.subtreeChildren.length > 0;
        const nextNodeMap = new Map<string, FeatureTreeNodeMeta>(projection.featureTreeNodeMap);
        let nextTreeData: TreeOption[] = projection.featureTreeData;

        if (!authoritativeGlbSubtree || (!hasBodyMerge && !hasSubtreeMerge)) {
            if (authoritativeGlbSubtree) {
                authoritativeGlbSubtree.nodeMapEntries.forEach((meta, key) => {
                    nextNodeMap.set(key, meta);
                });
            }
        } else {
            const nativeRoot = projection.featureTreeData[0];
            if (nativeRoot && nativeRoot.key === 'part-root') {
                const existingChildren: TreeOption[] = Array.isArray(nativeRoot.children)
                    ? (nativeRoot.children as TreeOption[])
                    : [];
                // 1. 把 GLB 叶子实体合并进 `solid-bodies-folder`，并同步更新「实体(N)」标签。
                const childrenWithMergedBodies = hasBodyMerge
                    ? existingChildren.map((child) => {
                        if (child && child.key === 'solid-bodies-folder') {
                            const nativeBodyChildren: TreeOption[] = Array.isArray(child.children)
                                ? (child.children as TreeOption[])
                                : [];
                            const visibleNativeBodyChildren = filterNativeBodyChildrenDuplicatedByGlbBodies(
                                nativeBodyChildren,
                                authoritativeGlbSubtree.bodyChildren,
                                nextNodeMap,
                                authoritativeGlbSubtree.nodeMapEntries,
                            );
                            const combined = resolveUniqueBodyTreeChildren([
                                ...visibleNativeBodyChildren,
                                ...authoritativeGlbSubtree.bodyChildren,
                            ]);
                            return {
                                ...child,
                                label: `实体(${combined.length})`,
                                children: combined,
                                isLeaf: combined.length === 0,
                            } as TreeOption;
                        }
                        return child;
                    })
                    : existingChildren;
                // 2. 把 GLB 子树（装配体结构等）追加到 part-root.children 的末尾。
                //    SolidWorks 的系统文件夹和基准项必须先展示，零件/装配结构跟在原点之后。
                const mergedChildren = hasSubtreeMerge
                    ? [...childrenWithMergedBodies, ...authoritativeGlbSubtree.subtreeChildren]
                    : childrenWithMergedBodies;
                const mergedRoot: TreeOption = {
                    ...nativeRoot,
                    children: mergedChildren,
                };
                nextTreeData = [mergedRoot, ...projection.featureTreeData.slice(1)];
            } else {
                nextTreeData = projection.featureTreeData;
            }
            authoritativeGlbSubtree.nodeMapEntries.forEach((meta, key) => {
                nextNodeMap.set(key, meta);
            });
        }
        const assemblyMateProjection = resolveAssemblyMateTreeProjection(nextNodeMap);
        assemblyMateProjection.featureTreeNodeMap.forEach((meta, key) => {
            nextNodeMap.set(key, meta);
        });
        nextTreeData = mergeAssemblyMateFoldersIntoTree(
            nextTreeData,
            assemblyMateProjection,
            nextNodeMap,
        );

        commitTreeRefreshSnapshot(
            refreshRequestId,
            targetTabId,
            nextTreeData,
            nextNodeMap,
            state,
        );

        return {
            objectCount: state.objectCount,
            commandCount: state.commandCount,
        };
    }

    /**
     * 按旧接口保留树构建入口。
     * @param objects 对象列表。
     * @param history 命令历史。
     */
    function buildFeatureTree(_objects: any[], _history: any[] = []): void {
        /**
         * 旧入口不能再消费外部 history。回退状态下 Worker history 只是 active prefix，
         * 直接投影会把完整 FeatureManager 时间线清空，导致首特征上方回退时特征消失。
         * 因此保留方法名兼容既有调用，但实际统一转向 Viewer 原子快照刷新。
         */
        void refreshTree(activeTreeTabId.value ?? partTabsStore.activeTabId).catch((error) => {
            logger.error('[FeatureTree] legacy buildFeatureTree atomic refresh failed', error);
        });
    }

    /**
     * 处理平铺树视图切换。
     * @param nextValue 是否切换为平铺视图。
     */
    async function handleToggleFlatTreeView(nextValue: boolean): Promise<void> {
        showFlatTreeView.value = nextValue;
        await refreshTree(activeTreeTabId.value ?? partTabsStore.activeTabId);
    }

    /**
     * 设置编辑零部件模式下的设计树名称高亮节点。
     * @param keys 需要高亮的设计树节点 key 集合。
     */
    function setAssemblyEditHighlightedTreeKeys(keys: Iterable<string>): void {
        assemblyEditHighlightedTreeKeys.value = new Set(keys);
    }

    /**
     * 清空编辑零部件模式下的设计树名称高亮节点。
     */
    function clearAssemblyEditHighlightedTreeKeys(): void {
        if (assemblyEditHighlightedTreeKeys.value.size === 0) {
            return;
        }
        assemblyEditHighlightedTreeKeys.value = new Set();
    }

    /**
     * 清空设计树当前选中态。
     *
     * 设计说明：
     * 1. 供文档上下文切换（Tab 切换）时主动收口 UI 选中投影；
     * 2. 不替代 Viewer 侧选择中心清空，两者在 Tab 切换链路中配合使用。
     */
    function clearSelection(): void {
        selectedKeys.value = [];
        selectedObject.value = null;
        planeStore.clearSelectedDatumPlanePreview();
    }

    const actions = createFeatureTreeActions({
        getViewer,
        featureTreeNodeMap,
        selectedKeys,
        selectedObject,
        currentDrawingDatumPlaneId,
        selectedDatumPlaneId,
        isSketchPlaneSelected,
        viewerMode,
        isDrawingMode,
        pendingSketchToolCommand,
        hasPendingSketchCommand,
        getMessage: () => messageProvider,
        getDialog: () => dialogProvider,
        getEnterSketchEditingOnDatumPlane: () => enterSketchEditingOnDatumPlaneProvider,
        getHandleSketchCommand: () => handleSketchCommandProvider,
        getHandleFeatureEdit: () => handleFeatureEditProvider,
        //------ 基准面编辑特征 开始------
        getHandleReferencePlaneEdit: () => handleReferencePlaneEditProvider,
        //------ 基准面编辑特征 结束------
        //------ 基准轴编辑特征 开始------
        getHandleReferenceAxisEdit: () => handleReferenceAxisEditProvider,
        //------ 基准轴编辑特征 结束------
        //------ 基准点编辑特征 开始------
        getHandleReferencePointEdit: () => handleReferencePointEditProvider,
        //------ 基准点编辑特征 结束------
        getHandleFeatureEditSketch: () => handleFeatureEditSketchProvider,
        getHandleSketchLifecycleAction: () => handleSketchLifecycleActionProvider,
        //------ 基准对象删除历史 开始------
        getHandleDatumReferenceDeleteAction: () => handleDatumReferenceDeleteActionProvider,
        //------ 基准对象删除历史 结束------
        getHandleFeatureDeleteAction: () => handleFeatureDeleteActionProvider,
        getHandleAssemblyComponentDeleteAction: () => handleAssemblyComponentDeleteActionProvider,
        getHandleFeatureVisibilityAction: () => handleFeatureVisibilityActionProvider,
        getHandleFeatureRollbackAction: () => handleFeatureRollbackActionProvider,
        getHandleAssemblyComponentFixationChange: () => handleAssemblyComponentFixationChangeProvider,
        getHandleAssemblyMateEdit: () => handleAssemblyMateEditProvider,
        //------ 线性零部件阵列节点删除 开始------
        getHandleLinearComponentPatternDelete: () => handleLinearComponentPatternDeleteProvider,
        getHandleLinearComponentPatternEdit: () => handleLinearComponentPatternEditProvider,
        //------ 线性零部件阵列节点删除 结束------
        clearPendingSketchToolCommand: () => sketchStateStore.clearPendingSketchToolCommand(),
        clearSelectedDatumPlanePreview: () => planeStore.clearSelectedDatumPlanePreview(),
        getSceneObjectById,
        getObjectById,
        refreshTree: (nameChange) => nameChange && applyNodeNameChange(activeTreeTabId.value, nameChange)
            ? Promise.resolve({objectCount: lastWorkerStateSummary?.objectCount ?? 0, commandCount: lastWorkerStateSummary?.commandCount ?? 0})
            : refreshTree(activeTreeTabId.value ?? partTabsStore.activeTabId),
        applyNodeVisibilityChange: (key, hidden) => applyNodeVisibilityChange(activeTreeTabId.value, key, hidden),
        requestRename,
        objectNameOverrides,
        resolveSketchEntityTypeByMeshClass,
    });

    return {
        featureTreeData,
        featureTreeRevision,
        selectedKeys,
        selectedObject,
        assemblyEditHighlightedTreeKeys,
        showFlatTreeView,
        isTreeLoading,
        isFeatureRollbackBarDisabled,
        activeTreeTabId,
        selectedObjectName,
        featureTreeNodeMap,
        hiddenObjectIds,
        deletedObjectIds,
        objectNameOverrides,
        sketchLifecycle,
        suppressedSketchIds,
        initProviders,
        registerGlbTreeRefreshProvider,
        unregisterGlbTreeRefreshProvider,
        buildFeatureTree,
        refreshTree,
        applyGlbNodePresentationUpdates,
        applyGlbRuntimeSubtreeUpdate,
        applyNodeVisibilityChange,
        applyNodeNameChange,
        applyAssemblyMateDiagnostics,
        applyAssemblyMateDocument,
        syncSketchSerial,
        getObjectDisplayName,
        getSceneObjectById,
        resolveSketchEntityTypeByMeshClass,
        handleToggleFlatTreeView,
        //------ 特征面草图绘制 开始------
        /**
         * 读取草图命令处理器。
         *
         * 设计说明：
         * 供视口右键菜单等非 FeatureTree 的 UI 组件直接调用草图工作流，
         * 无需通过 emit 链路层层透传至 Editor.vue。
         *
         * @returns 草图命令处理器；Provider 未注入时返回 null。
         */
        getHandleSketchCommand: () => handleSketchCommandProvider,
        //------ 特征面草图绘制 结束------
        setAssemblyEditHighlightedTreeKeys,
        clearAssemblyEditHighlightedTreeKeys,
        clearSelection,
        ...actions,
    };
});
